import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { validarFormulario } from "../utils/validators";
import { formatearFecha } from "../utils/formatters";
import { createEquipo, updateEquipo, deleteEquipo, createTicket } from "../services/dataService";
import { estadoColor } from "../utils/estadoColor";
import Card from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Table from "../components/ui/Table";
import SectionHeader from "../components/ui/SectionHeader";

export default function ModuloEquipos({ equipos, setEquipos, tickets, setTickets, usuarios = [], historialEquipos = [], agregarAlHistorial, registrarAuditoria, onNotify }) {
  const { puede, usuario } = useAuth();
  const [form, setForm] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [equipoSeleccionado, setEquipoSeleccionado] = useState(null);
  
  const [remitoModalOpen, setRemitoModalOpen] = useState(false);
  const [remitoModalSrc, setRemitoModalSrc] = useState("");
  
  const [formData, setFormData] = useState({ 
    serie: "", modelo: "", tipo: "", fabricante: "", cliente: "", 
    diagnostico_inicial: "",
    garantia: false,
    accesorios: "",
    remito_foto: "",
    ingreso: new Date().toISOString().slice(0, 10) 
  });
  const [errors, setErrors] = useState({});
  const [remitoPreview, setRemitoPreview] = useState("");

  const filtrados = equipos
    .filter(e =>
      e.serie?.toLowerCase().includes(search.toLowerCase()) ||
      e.modelo?.toLowerCase().includes(search.toLowerCase()) ||
      e.tipo?.toLowerCase().includes(search.toLowerCase()) ||
      e.cliente?.toLowerCase().includes(search.toLowerCase())
    )
    .slice().sort((a, b) => {
      const dateA = new Date(a.ingreso);
      const dateB = new Date(b.ingreso);
      if (isNaN(dateA) && isNaN(dateB)) return (b.created_at || 0) - (a.created_at || 0);
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;
      const diff = dateB - dateA;
      if (diff !== 0) return diff;
      return (b.created_at || 0) - (a.created_at || 0);
    });

  const resetForm = () => {
    setFormData({ 
      serie: "", modelo: "", tipo: "", fabricante: "", cliente: "", 
      diagnostico_inicial: "", garantia: false, accesorios: "", remito_foto: "",
      ingreso: new Date().toISOString().slice(0, 10) 
    });
    setRemitoPreview("");
    setErrors({});
    setEditing(null);
  };

  const handleRemitoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      onNotify?.("⚠️ La imagen es muy pesada (>2MB). Por favor comprimila o usá una foto más liviana.", "warning");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setRemitoPreview(reader.result);
      setFormData(prev => ({ ...prev, remito_foto: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!puede("crear") && !editing) { 
      onNotify?.("No tenés permiso para crear equipos", "error"); 
      return; 
    }
    if (!puede("editar") && editing) { 
      onNotify?.("No tenés permiso para editar equipos", "error"); 
      return; 
    }
    
    const errores = validarFormulario(formData, ['serie', 'modelo', 'cliente']);
    if (Object.keys(errores).length > 0) {
      setErrors(errores);
      onNotify?.("Verificá los campos obligatorios", "warning");
      return;
    }

    const duplicado = equipos.some(
      e => e.serie.toLowerCase().trim() === formData.serie.toLowerCase().trim() &&
           e.modelo.toLowerCase().trim() === formData.modelo.toLowerCase().trim() &&
           e.id !== editing?.id
    );
    
    if (!editing && duplicado) {
      onNotify?.("️ Ya existe un equipo registrado con ese N° de serie y Modelo", "warning");
      return;
    }
    
    try {
      if (editing) {
        await updateEquipo({ ...formData, id: editing.id });
        setEquipos(prev => prev.map(e => e.id === editing.id ? { ...formData, id: editing.id } : e));
        registrarAuditoria?.(`Equipo ${formData.serie} actualizado`, "Equipos");
        onNotify?.(`Equipo ${formData.serie} actualizado`, "success");
      } else {
        const saved = await createEquipo({ ...formData, estado: "Recepción", created_at: Date.now() });
        setEquipos(prev => [saved, ...prev]);
        registrarAuditoria?.(`Equipo ${formData.serie} registrado en Recepción`, "Equipos");
        
        if (agregarAlHistorial) {
          agregarAlHistorial(
            saved.id, saved.serie, "Ingreso",
            formData.diagnostico_inicial || "Ingreso inicial",
            usuario?.nombre || "Sistema", 
            formData.accesorios ? formData.accesorios.split(',').map(a => a.trim()) : []
          );
        }
        onNotify?.(`Equipo ${formData.serie} registrado en Recepción`, "success");
      }
    } catch (e) {
      onNotify?.(e.message, "error");
    }
    resetForm();
    setForm(false);
  };

  const handleEdit = (equipo) => {
    if (!puede("editar")) { 
      onNotify?.("No tenés permiso para editar", "error"); 
      return; 
    }
    setFormData({
      serie: equipo.serie, modelo: equipo.modelo, tipo: equipo.tipo || "", fabricante: equipo.fabricante || "",
      cliente: equipo.cliente, diagnostico_inicial: equipo.diagnostico_inicial || "",
      garantia: equipo.garantia || false, 
      accesorios: Array.isArray(equipo.accesorios) ? equipo.accesorios.join(", ") : (equipo.accesorios || ""),
      remito_foto: equipo.remito_foto || "", ingreso: equipo.ingreso
    });
    setRemitoPreview(equipo.remito_foto || "");
    setEditing(equipo);
    setForm(true);
    setErrors({});
  };

  const handleDelete = async (id, serie) => {
    if (!puede("eliminar")) { 
      onNotify?.("Solo administradores pueden eliminar", "error"); 
      return; 
    }
    if (window.confirm(`¿Eliminar permanentemente el equipo ${serie}?`)) {
      try {
        await deleteEquipo(id);
        setEquipos(prev => prev.filter(e => e.id !== id));
        registrarAuditoria?.(`Equipo ${serie} eliminado`, "Equipos");
        onNotify?.(`Equipo ${serie} eliminado`, "info");
      } catch (e) {
        onNotify?.(e.message, "error");
      }
    }
  };

  const handleReingreso = async (equipo) => {
    if (!puede("crear")) { 
      onNotify?.("No tenés permiso", "error"); 
      return; 
    }
    if (!window.confirm(`¿Registrar reingreso de ${equipo.serie}? Se creará un nuevo ticket y el equipo volverá a Recepción.`)) return;

    try {
      const ticketData = {
        equipoId: equipo.id, tecnico: "", prioridad: "Media",
        descripcion: `Reingreso - ${equipo.serie} (${equipo.modelo})`,
        fecha: new Date().toISOString().slice(0, 10),
        estado: "Recepción", creado_por: usuario?.nombre || "Sistema"
      };
      const savedTicket = await createTicket(ticketData);
      await updateEquipo({ id: equipo.id, estado: "Recepción" });

      if (setTickets) setTickets(prev => [savedTicket, ...prev]);
      setEquipos(prev => prev.map(e => e.id === equipo.id ? { ...e, estado: "Recepción" } : e));

      if (agregarAlHistorial) {
        agregarAlHistorial(equipo.id, equipo.serie, "Reingreso", `Reingreso - Ticket #${savedTicket.id}`, usuario?.nombre, []);
      }
      registrarAuditoria?.(`Reingreso: ${equipo.serie} → Ticket #${savedTicket.id}`, "Equipos");
      onNotify?.(`✅ Reingreso de ${equipo.serie} registrado. Ticket #${savedTicket.id} creado.`, "success");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  return (
    <div>
      <SectionHeader title={`Equipos (${filtrados.length})`} action={
        puede("crear") && (
          <Btn variant="primary" onClick={() => { setForm(f => !f); if (form) resetForm(); }}>
            {form ? "Cancelar" : "+ Nuevo equipo"}
          </Btn>
        )
      } />

      {form && (
        <Card className="form-card">
          <p className="form-card__title">{editing ? "✏️ Editar equipo" : "🆕 Nuevo equipo - Ingreso"}</p>
          <div className="grid grid--2 gap-2 mb-2">
            <Input value={formData.serie} onChange={e => { setFormData(p => ({ ...p, serie: e.target.value })); setErrors(pr => ({ ...pr, serie: null })); }} placeholder="N° de serie *" error={errors.serie} />
            <Input value={formData.modelo} onChange={e => { setFormData(p => ({ ...p, modelo: e.target.value })); setErrors(pr => ({ ...pr, modelo: null })); }} placeholder="Modelo *" error={errors.modelo} />
            <Input value={formData.tipo} onChange={e => setFormData(p => ({ ...p, tipo: e.target.value }))} placeholder="Tipo de equipo (Ej: Electrobisturí, Coagulador)" />
            <Input value={formData.fabricante} onChange={e => setFormData(p => ({ ...p, fabricante: e.target.value }))} placeholder="Fabricante" />
            <Input value={formData.cliente} onChange={e => setFormData(p => ({ ...p, cliente: e.target.value }))} placeholder="Cliente / Institución *" error={errors.cliente} />
            
            <div className="col-span-2 flex items-center gap-2 p-2 bg-primary-light rounded">
              <input type="checkbox" id="garantia" checked={formData.garantia} onChange={e => setFormData(p => ({ ...p, garantia: e.target.checked }))} className="w-4 h-4" />
              <label htmlFor="garantia" className="text-sm font-medium cursor-pointer">🔖 ¿El equipo ingresó por garantía?</label>
              {formData.garantia && <Badge color="success">Sí</Badge>}
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">📦 Accesorios que trajo el cliente</label>
              <textarea
                value={formData.accesorios}
                onChange={e => setFormData(p => ({ ...p, accesorios: e.target.value }))}
                placeholder="Ej: Tapa, cables de alimentación, manual de usuario, carrito..."
                rows={2}
                className="input w-full"
                style={{ resize: "vertical", fontFamily: "inherit" }}
              />
              <p className="text-xs text-muted mt-1">Separá los accesorios con comas</p>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">📄 Foto del Remito / Comprobante de ingreso</label>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleRemitoChange}
                className="input w-full text-sm"
              />
              <p className="text-xs text-muted mt-1">Formatos: JPG, PNG. Máx: 2MB</p>
              
              {remitoPreview && (
                <div className="mt-2 p-3 bg-surface rounded border" style={{ maxWidth: "100%", overflow: "hidden" }}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-shrink-0" style={{ width: "120px", maxWidth: "100%" }}>
                      <img 
                        src={remitoPreview} 
                        alt="Preview remito" 
                        className="rounded border cursor-pointer hover:opacity-80"
                        style={{ width: "100%", height: "120px", objectFit: "cover", maxWidth: "120px" }}
                        onClick={() => window.open(remitoPreview, '_blank')}
                      />
                    </div>
                    <div className="flex-1 min-w-0" style={{ minWidth: "200px" }}>
                      <p className="text-sm font-medium mb-1">✅ Remito cargado</p>
                      <p className="text-xs text-muted mb-2">Click en la imagen para ver en tamaño completo</p>
                      <Btn small variant="danger" onClick={() => { setRemitoPreview(""); setFormData(p => ({ ...p, remito_foto: "" })); }}>
                        🗑️ Eliminar foto
                      </Btn>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1"> Descripción del problema / Motivo de ingreso *</label>
              <textarea
                value={formData.diagnostico_inicial}
                onChange={e => setFormData(p => ({ ...p, diagnostico_inicial: e.target.value }))}
                placeholder="Ej: El cliente reporta que dejó de prender tras una caída..."
                rows={4}
                className="input w-full"
                style={{ resize: "vertical", fontFamily: "inherit" }}
              />
            </div>
            <Input value={formData.ingreso} onChange={e => setFormData(p => ({ ...p, ingreso: e.target.value }))} type="date" />
          </div>
          <div className="flex gap-2">
            <Btn variant="primary" onClick={handleSubmit}>{editing ? "Actualizar" : "Registrar en Recepción"}</Btn>
            <Btn variant="default" onClick={() => { setForm(false); resetForm(); }}>Cancelar</Btn>
          </div>
        </Card>
      )}

      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Buscar por serie, modelo, tipo o cliente..." className="mb-3" />

      <Card>
        <Table
          cols={["N° Serie", "Tipo", "Modelo", "Cliente", "Estado", "Ingreso", "Accesorios", "Remito", "Acciones"]}
          rows={filtrados.map(e => [
            <code className="code-badge">{e.serie}</code>,
            e.tipo || <span className="text-xs text-muted">—</span>,
            <strong>{e.modelo}</strong>,
            e.cliente || "—",
            <Badge color={estadoColor(e.estado)}>{e.estado}</Badge>,
            <span className="text-xs text-muted">{formatearFecha(e.ingreso)}</span>,
            <span className="text-xs" title={Array.isArray(e.accesorios) ? e.accesorios.join(", ") : e.accesorios || "Sin accesorios"}>
              {Array.isArray(e.accesorios) 
                ? `${e.accesorios.slice(0, 2).join(", ")}${e.accesorios.length > 2 ? '...' : ''}` 
                : (e.accesorios ? `${e.accesorios.slice(0, 20)}${e.accesorios.length > 20 ? '...' : ''}` : "—")}
            </span>,
            e.remito_foto ? (
              <Btn small variant="default" onClick={() => { setRemitoModalOpen(true); setRemitoModalSrc(e.remito_foto); }}>
                📄 Ver Remito
              </Btn>
            ) : (
              <span className="text-xs text-muted">—</span>
            ),
            <div className="flex gap-1">
              <Btn small variant="default" onClick={() => setEquipoSeleccionado(e)}>📜 Historial</Btn>
              {puede("editar") && <Btn small title="Editar" onClick={() => handleEdit(e)}>✏️</Btn>}
              {(e.estado === "Reparado" || e.estado === "Entregado") && puede("crear") && (
                <Btn small variant="primary" title="Reingreso" onClick={() => handleReingreso(e)}>🔄 Reingreso</Btn>
              )}
              {puede("eliminar") && <Btn small variant="danger" title="Eliminar" onClick={() => handleDelete(e.id, e.serie)}>🗑️</Btn>}
            </div>
          ])}
        />
      </Card>

      {/* MODAL DE REMITO */}
      {remitoModalOpen && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "20px" }} onClick={() => setRemitoModalOpen(false)}>
          <div style={{ backgroundColor: "white", borderRadius: "12px", maxWidth: "900px", width: "100%", maxHeight: "85vh", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 20px", backgroundColor: "#0f4c81", color: "white" }}>
              <h3 style={{ margin: 0, fontSize: "18px" }}>📄 Remito de Ingreso</h3>
              <button onClick={() => setRemitoModalOpen(false)} style={{ background: "none", border: "none", color: "white", fontSize: "24px", cursor: "pointer", padding: "0 8px" }}>✕</button>
            </div>
            <div style={{ padding: "30px", backgroundColor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
              <img src={remitoModalSrc} alt="Remito" style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain", boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "4px" }} />
            </div>
            <div style={{ padding: "15px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setRemitoModalOpen(false)} style={{ padding: "8px 20px", backgroundColor: "#e5e7eb", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "14px" }}>✓ Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* PANEL DE HISTORIAL */}
      {equipoSeleccionado && (
        <Card className="mt-4 border-l-4 border-primary">
          <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-lg">📜 Historial de Intervenciones</h3>
              <p className="text-sm text-muted">Equipo: <strong>{equipoSeleccionado.serie}</strong> · {equipoSeleccionado.modelo} {equipoSeleccionado.tipo && `(${equipoSeleccionado.tipo})`}</p>
              {equipoSeleccionado.accesorios && (
                <p className="text-xs text-muted mt-1">
                  📦 Accesorios: {Array.isArray(equipoSeleccionado.accesorios) ? equipoSeleccionado.accesorios.join(", ") : equipoSeleccionado.accesorios}
                </p>
              )}
              {equipoSeleccionado.remito_foto && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-muted">📄 Remito:</span>
                  <Btn small variant="default" onClick={() => { setRemitoModalOpen(true); setRemitoModalSrc(equipoSeleccionado.remito_foto); }}>Ver Remito</Btn>
                </div>
              )}
            </div>
            <Btn small variant="default" onClick={() => setEquipoSeleccionado(null)}>✕ Cerrar</Btn>
          </div>

          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tipo</th>
                  <th>Detalle</th>
                  <th>Técnico</th>
                  <th>Repuestos</th>
                </tr>
              </thead>
              <tbody>
                {historialEquipos
                  .filter(h => h.equipo_id === equipoSeleccionado.id)
                  .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                  .map(h => (
                    <tr key={h.id}>
                      <td className="text-xs text-muted whitespace-nowrap">{h.fecha}</td>
                      <td><Badge color={h.tipo === "Ingreso" ? "primary" : h.tipo === "Entrega" ? "success" : "warning"}>{h.tipo}</Badge></td>
                      <td className="max-w-xs truncate" title={h.detalle}>{h.detalle}</td>
                      <td>{h.tecnico}</td>
                      <td className="text-xs">{h.repuestos?.length > 0 ? h.repuestos.join(", ") : "—"}</td>
                    </tr>
                  ))}
                {historialEquipos.filter(h => h.equipo_id === equipoSeleccionado.id).length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">Sin registros aún</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}